<template>
	<div>
		<SellHead v-on:checHead="checHead"
				  :gridApi="gridApi"
				  :has-unhandled-investors="showUnhandledInvestorsHint"
		></SellHead>
		<div class="flex" v-if="tableReady">
			<div class="flex flex_column w_100">
				<AgItem
					v-on:getGridApi="getGridApi"
					:rowData="rowData"
					:columnDefs="columnDefs"
					v-on:sortChanged="sortChanged"
					:frameworkComponents="frameworkComponents"
					:with-tabs="true"
					:with-hint-box="showUnhandledInvestorsHint"
					:with-header="true"
				></AgItem>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
		</div>
		<SalesParametersCalculationModal v-if="salesParametersModalShown"
										 :data="salesParametersCalculationData"
										 @close="closeSalesParametersModal"
		></SalesParametersCalculationModal>
	</div>
</template>
<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import SellHead from "@/components/templates/seller/sell/SellHead.vue";
import {receivableApi} from "@/common/api/receivableApi";
import {sellApi} from "@/common/api/sellApi";
import {asLocalizedDate, asMoneyAmount, asPercentage} from "@/components/templates/agGrid/formatter/valueFormatters";
import Modal from "@/components/templates/modal/Modal.vue";
import SalesParametersCalculationModal from "@/components/templates/seller/sell/SalesParametersCalculationModal.vue";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import {enumDef} from "@/common/enumDefs";
import {whitelistApi} from "@/common/api/whitelistApi";
import {hasAccess} from "@/common/permission/permission";
import {startPriceCalculator} from "@/components/templates/seller/sell/startPriceCalculator";
import EnumAsText from "@/components/templates/agGrid/renderer/EnumAsText.vue";
import {formatAsDateForAPI} from "@/common/format";
import moment_timezone from "moment-timezone";

export default {
	extends: PaginationParent,
	props: {
		frameworkComponents: Object,
	},
	components: { AgItem, SellHead, Modal, SalesParametersCalculationModal, Pagination },
	data() {
		return {
			rowData: [],
			columnDefs: [],
			sorting: [],
			gridApi: null,
			salesParametersCalculationData: null,
			salesParametersModalShown: false,
			unhandledInvestorsCount: null
		};
	},
	computed: {
		showUnhandledInvestorsHint() {
			// Investor list feature is disabled for now until feature redesign, so we enforce to never show this hint until then
			return false;
			// return this.unhandledInvestorsCount > 0;
		},
		tableReady() {
			return this.unhandledInvestorsCount !== null;
		}
	},
	created() {
		this.loadUnhandledInvestorsCount();
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		checHead(data) {
			let { checkData, type, isRetentionAllowed } = data;

			switch (type) {
				case "del":
					this.confirmForReset(checkData, true);
					break;
				case "share":
					this.confirmForStartAuction(checkData, true);
					break;
				case "bundle":
				case "smartBundle":
					this.bundleFun(checkData, type, isRetentionAllowed);
					break;
				case "setData":
					this.showSalesParametersModal(checkData);
					break;
				default:
					break;
			}
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		showSalesParametersModal(data) {
			// If no specific row data is given, then we calculate for all
			if (!data || data.length === 0) {
				data = this.rowData;
			}

			this.salesParametersCalculationData = data;
			this.salesParametersModalShown = true;
		},
		closeSalesParametersModal() {
			this.salesParametersModalShown = false;
		},
		getGridApi(gApi) {
			this.gridApi = gApi;
		},
		setColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t("common.debtor"),
					children: [
						{
							headerName: this.$t("common.debtor"),
							field: "obligorName",
							minWidth: 175,
							sortable: true,
							nameType: "obligorName",
							idType: "obligorId",
							cellRendererFramework: "CompanyTem"
						},
						{
							headerName: this.$t("common.cofaceDra"),
							headerComponentParams: {
								tooltip: this.$t("seller.sell.table.header.dra.tooltip"),
							},
							field: "debtorCofaceDra",
							minWidth: 70,
							maxWidth: 70,
							cellRendererFramework: "CofaceDraRenderer",
							cellRendererParams: {
								draStatusField: "debtorCofaceDraStatus"
							},
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.ipu"),
							headerComponentParams: {
								tooltip: this.$t("seller.sell.table.header.ipu.tooltip")
							},
							minWidth: 60,
							maxWidth: 100,
							columnGroupShow: "open",
							cellRendererFramework: "IPUView"
						},
						{
							headerName: this.$t("common.latePaymentDays"),
							headerComponentParams: {
								tooltip: this.$t("seller.sell.table.header.latePaymentDays.tooltip")
							},
							field: "dlq",
							columnGroupShow: "open",
							minWidth: 60,
							maxWidth: 100,
						}
					]
				},

				{
					headerName: this.$t("common.receivableDetails"),
					children: [
						{
							headerName: this.$t("common.type"),
							field: "type",
							minWidth: 65,
							maxWidth: 80,
							sortable: true,
							cellRendererFramework: "EnumAsIcon",
							enumDef: enumDef.receivableType
						},
						{
							headerName: this.$t("common.invoiceNumber"),
							minWidth: 175,
							field: "invoiceNumber",
							sortable: true,
							cellRendererFramework: "ItemDetail",
						},
						{
							headerName: this.$t("common.dueDate"),
							field: "dueDate",
							valueFormatter: asLocalizedDate(),
							sortable: true,
							minWidth: 100,
							maxWidth: 120,
						},
						{
							headerName: this.$t("common.amount"),
							headerClass: "align-right",
							field: "amount",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							maxWidth: 150,
							valueFormatter: asMoneyAmount(),
							sortable: true,
						},
						{
							headerName: this.$t("common.ccy"),
							minWidth: 70,
							maxWidth: 70,
							field: "currency",
							sortable: true,
						},
						{
							headerName: this.$t("common.assignmentType"),
							field: "disclosed",
							cellRendererFramework: "EnumAsText",
							enumDef: enumDef.disclosedType,
							minWidth: 140,
						}
					]
				},
				{
					headerName: this.$t("seller.sell.ApprovedExchange.salesParameters"),
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("seller.sell.table.header.retention"),
							headerComponentParams: {
								tooltip: this.$t("seller.sell.table.header.retention.tooltip")
							},
							headerClass: "align-left",
							field: "allowedRetentionRate",
							minWidth: 90,
							valueFormatter: asPercentage({ default: "0", decimals: 0 })
						},
						{
							headerComponentFramework: "ApplyValue",
							headerComponentParams: {
								valueFormatted: this.$t("seller.sell.table.header.recommendedDiscount"),
								showAlways: true,
								apply: this.applyRecommendedDiscounts
							},
							headerClass: "align-left",
							field: "recommendedDiscount",
							minWidth: 200,
							valueFormatter: asPercentage(),
							cellRendererFramework: "ApplyValue",
							cellRendererParams: {
								targetField: "instantBuyPercentage",
								asPercentage: true,
								iconConfig: {
									iconClass: "fa fa-warning text-warning",
									iconTooltip: this.$t('seller.priceSuggestion.unknownRating.tooltip'),
									showCondition: (data) => !data.debtorHasKnownRating
								}
							}
						},
						{
							headerName: this.$t("seller.sell.table.header.instantBuyDiscountInPercentage"),
							headerComponentParams: {
								tooltip: this.$t("seller.sell.table.header.instantBuyDiscountInPercentage.tooltip")
							},
							minWidth: 160,
							field: "instantBuyPercentage",
							cellRendererFramework: "PriceInput",
							cellRendererParams: {
								valuePlaceholder: "seller.sell.table.value.instantBuyDiscountInPercentage.tooltip"
							}
						},
						{
							headerName: this.$t("seller.sell.ApprovedExchange.saleDuration"),
							minWidth: 140,
							field: "durationInHours",
							cellRendererFramework: "DurrationSelect",
						}
					]
				},
				{
					headerName: this.$t("common.actions"),
					headerClass: "bg_special",
					children: [
						{
							minWidth: 120,
							pageType: "sell",
							cellRendererFramework: "SellTableOperation",
							delFun: data => {
								this.confirmForReset(data, false);
							},
							setFun: data => {
								this.confirmForStartAuction(data, false);
							},
							unbundle: this.unbundle,
							sellWithOptionFun: this.confirmSellWithOption
						},
					]
				},
				{
					headerClass: "bg_special",
					children: [
						{
							minWidth: 45,
							maxWidth: 45,
							checkboxSelection: true,
							pinned: "right",
							headerCheckboxSelection: true,
							headerCheckboxSelectionFilteredOnly: true
						}
					]
				}
			];
		},
		bundleFun(data, type, isRetentionAllowed) {
			let func;
			if (type == "smartBundle") {
				func = receivableApi.smartBundle(isRetentionAllowed);
			} else {
				if (!data || data.length <= 1) {
					this.toast(this.$t("SellTable.Bundle.errorMsg"));
					return;
				}
				let option = [];
				for (let entry of data) {
					option.push(entry.id);
				}
				func = receivableApi.bundle(option, isRetentionAllowed);
			}
			func.then(res => {
					this.getList();
					this.dataChanged();
				})
				.catch(err => {
					this.toast(err);
				});
		},
		confirmForReset(data, isAll) {
			if (isAll && (!data || data.length === 0)) {
				return;
			}

			let option = {
				title: "",
				msg: this.$t("seller.sell.ApprovedExchange.reset.confirmation"),
				confirmFun: () => {
					this.resetFun(data, isAll);
				}
			};
			this.confirmMeg(option, true);
		},
		resetFun(data, isAll) {
			// reset receivable
			let option = [];
			if (!isAll) {
				option.push(data.id);
			} else {
				for (let entry of data) {
					option.push(entry.id);
				}
			}
			receivableApi.resetOperation(option)
				.then(res => {
					this.getList();
					this.dataChanged();
				})
				.catch(err => {
					this.toast(err);
				});
		},
		confirmForStartAuction(data, isAll) {
			if (isAll && (!data || data.length === 0)) {
				return;
			}

			let option = {
				title: "",
				msg: this.$t("seller.sell.ApprovedExchange.start.confirmation"),
				confirmFun: () => {
					this.startAuctionFun(data, isAll);
				}
			};
			this.confirmMeg(option, true);
		},
		startAuctionFun(data, isAll) {
			let option = [];
			if (!isAll) {
				option.push(this.buildStartAuctionData(data));
			} else {
				for (let entry of data) {
					option.push(this.buildStartAuctionData(entry));
				}
			}
			for (let entry of option) {
				let durationInHoursError = !entry.durationInHours;
				let startTimeError = !entry.startTime;

				let priceError = !entry.price && !entry.instantBuyPrice;

				if (durationInHoursError) {
					this.toast(
						this.$t("seller.sell.startAuction.error.DurationRequired")
					);
				}
				if (startTimeError) {
					this.toast(
						this.$t("seller.sell.startAuction.error.StartTimeRequired")
					);
				}
				if (priceError) {
					this.toast(this.$t("seller.sell.startAuction.error.PriceRequired"));
				}
				if (durationInHoursError || startTimeError || priceError) {
					return;
				}
			}
			sellApi.operation.startAuction(option)
				.then(res => {
					if (res.status !== "failed") {
						this.getList();
						this.dataChanged();
					} else {
						this.toast(res.failedStartsMessages);
					}
				})
				.catch(err => {
					this.toast(err);
				});
		},
		unbundle(data) {
			receivableApi.unbundle(data.id)
				.then(res => {
					this.getList();
					this.dataChanged();
				})
				.catch(err => {
					this.toast(err);
				});
		},
		confirmSellWithOption(data) {
			let option = {
				title: "",
				msg: this.$t("seller.sell.ApprovedExchange.revolvingSale.msg"),
				confirmFun: this.sellWithOption,
				params: data
			};
			this.confirmMeg(option, true);
		},
		sellWithOption(data) {
			sellApi.operation.sellWithOption(data.id, data.optionId)
				.then(() => {
					this.getList();
					this.dataChanged();
				})
				.catch(this.toast);
		},
		getList() {
			let postData = {
				columns: [],
				filters: [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting
			};

			sellApi.inspection.listApproved(postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data.map(item => {
						return {
							...item,
							// Make sure data for input field cell renderers is initialized before the table renders its data
							price: item.price || null,
							instantBuyPrice: item.instantBuyPrice || null,
							instantBuyPercentage: null,
							startTime: "",
							durationInHours: item.auctionDurationInHours || enumDef.SellDurationOptions.properties[enumDef.SellDurationOptions.default].value
						};
					});
				})
				.catch(err => {
					this.toast(err);
				});
		},
		loadUnhandledInvestorsCount() {
			if (hasAccess(this.currentUser, enumDef.permissions.SellerInvestorManagement)) {
				whitelistApi.unhandledCount()
					.then((res) => {
						this.unhandledInvestorsCount = res;
					})
					.catch(this.toast)
			} else {
				// Fake no unhandled investors so no hint is shown, but data counts as loaded
				this.unhandledInvestorsCount = 0;
			}
		},
		dataChanged() {
			this.$emit("changed");
		},
		buildStartAuctionData(data) {
			return {
				receivableId: data.id,
				currency: data.currency,
				invoiceNumber: data.invoiceNumber,
				price: null,
				instantBuyPrice: data.instantBuyPercentage > 0 ? startPriceCalculator.calculateWithDiscount(data, data.instantBuyPercentage) : null,
				startTime: formatAsDateForAPI(moment_timezone()
					.tz("Europe/Berlin")
					.add(enumDef.nowAdjustment.value, enumDef.nowAdjustment.unit), { withTime: true }),
				durationInHours: data.durationInHours,
				disclosed: data.disclosed
			};
		},
		applyRecommendedDiscounts(params) {
			params.api.forEachNode(rowNode => {
				rowNode.data.instantBuyPercentage = rowNode.data.recommendedDiscount * 100;
			});
		},
	}
};
</script>
